import * as React from "react";
import { Button, Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { AppBody } from "../../AppBody";
import Breadcrumbs from "../../../containers/Breadcrumbs";
import { appHeaderTitle } from "../../../common/services/Utilities";
import HelpButton from "../../HelpContent/HelpButton";
import SummaryTablesSearchForm from "../../../containers/SummaryTablesSearchForm";
import { KeyValue, Query } from "../../../interfaces";
import BackButton from "../../../containers/BackButton";
import SummaryTablesByCharter from "./SummaryTablesByCharter";
import SummaryTablesByAsset from "./SummaryTablesByAsset";
import { useHistory } from "react-router-dom";
import SummaryTablesByState from "./SummaryTablesByState";
import useSortProperties from "../../../hooks/useSortProperties";
import SummaryTablesByStateCharter from "./SummaryTablesByStateCharter";
import SummaryTablesTop50 from "./SummaryTablesTop50";
import SummaryTablesByCounty from "./SummaryTablesByCounty";
import SummaryTablesTimeSeries from "./SummaryTablesTimeSeries";
import SummaryTablesByMSA from "./SummaryTablesByMSA";
import { initialAppState } from "../../../common/reducers/appState";
import SummaryLevelTwo from "../../../containers/SummaryLevelTwo";
import CloudFoundryBanner from "../../../containers/CloudFoundryBanner";

interface Props {
  appState: Query;
  resetState: (state: Query) => void;
  updateSort: (sortField: string, sortDirection: string) => void;
}
const useStyles = makeStyles(theme => ({
  body: {
    color: theme.palette.primary.main,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "24px",
    fontStyle: "normal",
  },
  header: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "24px",
    fontStyle: "normal",
  },
}));

export default function SummaryTables({
  appState,
  resetState,
  updateSort,
}: Props) {
  const classes = useStyles();
  const history = useHistory();
  const sortProperties = useSortProperties(appState, updateSort);
  const introParagraph = <>{appHeaderTitle("summaryTables")}</>;
  const reportType = appState.ui.reportType;
  const [previousReportType, setPreviousReportType] = React.useState(
    reportType,
  );

  React.useEffect(() => {
    if (reportType !== previousReportType) {
      const defaultState = {
        ...initialAppState.SOD,
        filters: {
          ...initialAppState.SOD.filters,
          lastYear: appState.filters.lastYear,
          startDate: appState.filters.startDate
            ? appState.filters.startDate
            : "1994",
          endDate: appState.filters.endDate ? appState.filters.endDate : "1994",
        },
        ui: {
          ...initialAppState.SOD.ui,
          reportType: appState.ui.reportType,
        },
      };
      setPreviousReportType(reportType);
      if (reportType === "nationalTimeSeries") {
        resetState({
          ...defaultState,
          filters: {
            ...defaultState.filters,
            startDate: appState.filters.startDate,
            endDate: appState.filters.endDate,
          },
          ui: {
            ...defaultState.ui,
            totalsType: appState.ui.totalsType,
            institutionTypeTimeSeries: appState.ui.institutionTypeTimeSeries,
          },
        });
      } else if (reportType === "stateCharterClass") {
        resetState({
          ...defaultState,
          filters: {
            ...defaultState.filters,
            stalpbr: appState.filters.stalpbr,
          },
        });
      } else if (reportType === "stateCounty") {
        resetState({
          ...defaultState,
          filters: {
            ...defaultState.filters,
            stalpbr: appState.filters.stalpbr,
            county: appState.filters.county,
          },
        });
      } else {
        resetState(defaultState);
      }
    }
  }, [appState, previousReportType, reportType, resetState]);

  React.useEffect(() => {
    if (!appState.filters.county && !appState.ui.summaryTableLevelTwo) {
      appState.filters.countyReset = true;
    }
    if (appState.filters.county && !appState.ui.summaryTableLevelTwo) {
      appState.filters.countyReset = false;
    }
  }, [appState.filters, appState.ui]);

  const summaryTableReturn = (reportType: string) => {
    const lookup: KeyValue = {
      charterClass: (
        <>
          {appState.ui.summaryTableLevelTwo ? (
            <SummaryLevelTwo sortProperties={sortProperties} />
          ) : (
            <SummaryTablesByCharter appState={appState} />
          )}
        </>
      ),
      assetSize: (
        <>
          {appState.ui.summaryTableLevelTwo ? (
            <SummaryLevelTwo sortProperties={sortProperties} />
          ) : (
            <SummaryTablesByAsset appState={appState} />
          )}
        </>
      ),
      metroStatisticalArea: (
        <>
          {appState.ui.summaryTableLevelTwo ? (
            <SummaryLevelTwo sortProperties={sortProperties} />
          ) : (
            <SummaryTablesByMSA appState={appState} />
          )}
        </>
      ),
      stateCharterClass: (
        <>
          {appState.ui.summaryTableLevelTwo ? (
            <SummaryLevelTwo sortProperties={sortProperties} />
          ) : (
            <SummaryTablesByStateCharter appState={appState} />
          )}
        </>
      ),
      stateCounty: (
        <>
          {appState.ui.summaryTableLevelTwo ? (
            <SummaryLevelTwo sortProperties={sortProperties} />
          ) : (
            <SummaryTablesByCounty appState={appState} />
          )}
        </>
      ),
      stateAndTerritories: (
        <>
          {appState.ui.summaryTableLevelTwo ? (
            <SummaryLevelTwo sortProperties={sortProperties} />
          ) : (
            <SummaryTablesByState appState={appState} />
          )}
        </>
      ),
      top50CommercialBanks: <SummaryTablesTop50 appState={appState} />,
      top50BHC: <SummaryTablesTop50 appState={appState} />,
      nationalTimeSeries: (
        <>
          {appState.ui.summaryTableLevelTwo ? (
            <SummaryLevelTwo sortProperties={sortProperties} />
          ) : (
            <SummaryTablesTimeSeries appState={appState} />
          )}
        </>
      ),
    };
    return lookup[reportType] ? (
      lookup[reportType]
    ) : (
      <>
        <Typography id="summary-tables-description" className={classes.body}>
          The Summary Tables provide annual branch office deposits as of June
          30th. Select your criteria to left, then generate the report to view
          your results. You can view the data for specified years with a range
          of selectable aggregations including:
        </Typography>
        <br />
        <br />
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography
              id="summary-tables-national-summary-header"
              className={classes.header}
            >
              National Summary Tables
            </Typography>
            <Typography
              id="summary-tables-national-summary-description"
              className={classes.body}
              component="div"
            >
              Choose from a number of popular aggregations for a national view
              of:
              <ul>
                <li>Bank Charter Class</li>
                <li>Asset Size</li>
                <li>Top 50 Bank Holding Companies by Deposits</li>
                <li>
                  Top 50 Commercial Banks and Savings Institutions by Deposits
                </li>
                <li>State Totals</li>
                <li>
                  States by Year Time Series, within a chosen range of years
                </li>
              </ul>
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              id="summary-tables-regional-msa-header"
              className={classes.header}
            >
              Regional/MSA Summary Tables
            </Typography>
            <Typography
              id="summary-tables-regional-msa-description"
              className={classes.body}
              component="div"
            >
              View regional data by Metropolitan Statistical Area and compare
              values by:
              <ul>
                <li>All Institutions</li>
                <li>Commercial Banks</li>
                <li>Savings Banks</li>
                <li>U.S. Branches of Foreign Banks</li>
              </ul>
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              id="summary-tables-state-header"
              className={classes.header}
            >
              Individual State
            </Typography>
            <Typography
              id="summary-tables-state-description"
              className={classes.body}
              component="div"
            >
              View data for individual states with results display as either
              option of:
              <ul>
                <li>County</li>
                <li>
                  Bank Charter Class with details of bank headquarters in-state,
                  out-of-state, and summary totals
                </li>
              </ul>
            </Typography>
          </Grid>
        </Grid>
      </>
    );
  };
  const stateValue =
    appState.ui.reportType === "stateCharterClass" ||
    appState.ui.reportType === "stateCounty" ||
    appState.ui.reportType === "nationalTimeSeries"
      ? `&stalpbr=${appState.filters.stalpbr}`
      : "";
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle="Summary Tables" />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        introText={introParagraph}
        searchForm={<SummaryTablesSearchForm />}
        backButton={
          <BackButton app="SOD" buttonLabel="Back to SOD" variant="dark" />
        }
      >
        {appState.ui.summaryTableLevelTwo && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              if (appState.filters.countyReset === true) {
                appState.filters.county = "";
              }
              resetState({
                ...appState,
                ui: {
                  ...appState.ui,
                  summaryTableLevelTwo: false,
                  summaryTableType: "",
                },
                pagination: {},
              });
              history.push({
                search: `?lastYear=${appState.filters.lastYear}&reportType=${appState.ui.reportType}${stateValue}`,
              });
            }}
          >
            Back to Summary
          </Button>
        )}
        {summaryTableReturn(appState.ui.reportType)}
      </AppBody>
    </>
  );
}
